import { callApi, ThrowError } from '@/api/callApi';
import { API_ENDPOINT, API_URL_USER_SERVICE } from '@/api/constant';
import CommonFilter from '@/components/common/CommonFilter';
import CommonTable, {
  Action,
  TableColumn,
} from '@/components/common/CommonTable';
import ExportOption from '@/components/common/export/ExportOption';
import ConfirmationModel from '@/components/common/models/ConfrimationModel';
import PageHeading from '@/components/common/PageHeading';
import Pagination from '@/components/common/Pagination';
import StatusDropdown from '@/components/common/StatusComponent';
import Layout from '@/components/Layout/Layout';
import Card from '@/components/UI/Card';
import DateRangePicker from '@/components/UI/DateRange';
import { useDebounce } from '@/hooks/useDebounce';
import { useSelectableTable } from '@/hooks/useSelectableTable';
import {
  Customer,
  ICustomerResponse,
  ICustomerStates,
} from '@/interfaces/customer.interface';
import { UserRoles } from '@/utility/enum';
import {
  GetFilterUrl,
  showToastNotification,
  toFixedThree,
} from '@/utility/helper';
import { RoutesLink } from '@/utility/RouteLinks';
import { useEffect, useState } from 'react';
import { Range } from 'react-date-range';
import { useNavigate } from 'react-router-dom';

const Customers: React.FC = () => {
  const navigate = useNavigate();
  const [searchValue, setSearchValue] = useState<string>('');
  const debounceValue = useDebounce(searchValue);

  const [state, setState] = useState<ICustomerStates>({
    customer: [],
    pageInfo: {
      total: 0,
      totalPages: 0,
      limit: 10,
      page: 1,
    },
    type: '',
    roleId: '',
    isLoading: false,
    selectedUserId: null,
    showStatusModel: false,
    showVerificationModel: false,
    statusToUpdate: null,
  });

  const [dateRange, setDateRange] = useState<Range[]>([
    {
      startDate: new Date(new Date().getFullYear(), new Date().getMonth(), 1),
      endDate: new Date(),
      key: 'selection',
    },
  ]);

  const { selectedItems, handleSelectAll, handleSelectItem } =
    useSelectableTable(state.customer);

  useEffect(() => {
    fetchCustomers();
  }, [
    dateRange,
    state.pageInfo.limit,
    state.pageInfo.page,
    debounceValue,
    state.type,
    state.roleId,
  ]);

  const handleVerifyUser = async (id: string, status: string) => {
    try {
      const PATH = `${API_URL_USER_SERVICE}${API_ENDPOINT.users}/verify-user/${id}`;
      const isVerified = status === 'verified' ? true : false;
      await callApi(PATH, {
        method: 'PATCH',
        data: { isVerified: isVerified },
      });
      fetchCustomers();
    } catch (error) {
      ThrowError(error);
    }
  };

  const fetchCustomers = async () => {
    try {
      const filterQuery = {
        page: state.pageInfo.page,
        limit: state.pageInfo.limit,
        fullName: debounceValue,
        type: state.type,
        roleId: state.roleId,
      };
      const query = GetFilterUrl(filterQuery);
      setState((prev) => ({
        ...prev,
        isLoading: true,
      }));
      const PATH = `${API_URL_USER_SERVICE}${API_ENDPOINT.users}/list?${query ? `${query}` : ''}`;
      const res = await callApi<ICustomerResponse>(PATH, { method: 'GET' });
      setState((prev) => ({
        ...prev,
        customer: res?.data,
        pageInfo: {
          limit: +res.pageInfo.limit,
          page: +res.pageInfo.page,
          total: +res.pageInfo.total,
          totalPages: +res.pageInfo.totalPages,
        },
        isLoading: false,
      }));
    } catch (error) {
      console.error('Failed to fetch customers:', error);
      setState((prev) => ({
        ...prev,
        isLoading: false,
      }));
    }
  };

  const handleChangePage = (page: number) => {
    setState((prev) => ({
      ...prev,
      pageInfo: {
        ...prev.pageInfo,
        page: page,
      },
    }));
  };

  const handleChangePageSize = (limit: number) => {
    setState((prev) => ({
      ...prev,
      pageInfo: {
        ...prev.pageInfo,
        page: 1,
        limit: limit,
      },
    }));
  };

  const handleChangeTab = (filter: string) => {
    setState((prev) => ({
      ...prev,
      type: filter,
      roleId: '',
      pageInfo: {
        ...prev.pageInfo,
        page: 1,
      },
    }));
  };

  const handleBlockUser = async (
    userId: string,
    status: 'blocked' | 'active',
  ) => {
    try {
      const isBlocked = status === 'blocked' ? true : false;
      const PATH = `${API_URL_USER_SERVICE}${API_ENDPOINT.users}/block/${userId}?isBlocked=${isBlocked}`;
      await callApi(PATH, { method: 'PATCH' });
      handleChangePage(1);
      fetchCustomers();
    } catch (error) {
      const err = error as { message: string };
      showToastNotification('danger', err.message);
      console.log('error in block', error);
    }
  };

  const filters = [
    { label: 'All', value: '' },
    { label: 'Customers', value: 'customer' },
    { label: 'Employee', value: 'employee' },
  ];

  const customerColumns: TableColumn<Customer>[] = [
    {
      key: 'name',
      header: 'Name',
      render: (customer) => (
        <span
          className={customer?.isEmployee ? 'text-blue-500' : ''}
        >{`${customer?.fullName}`}</span>
      ),
      className: 'p-3 font-medium text-gray-900 ',
    },
    {
      key: 'type',
      header: 'Type',
      render: (customer) => {
        return (
          <>
            <StatusDropdown
              options={[
                {
                  label: 'Verified',
                  value: 'verified',
                  textClass: 'text-yellow-500',
                  bgClass: 'bg-yellow-500',
                },
                {
                  label: 'Unverified',
                  value: 'unVerified',
                  textClass: 'text-purple-500',
                  bgClass: 'bg-purple-500',
                },
              ]}
              canEditVal="user"
              value={customer?.isVerified ? 'verified' : 'unVerified'}
              onChange={(value) =>
                setState((prev) => ({
                  ...prev,
                  selectedUserId: customer.id,
                  statusToUpdate: value,
                  showVerificationModel: true,
                }))
              }
            />
            {/* <span className={customer.isEmployee ? 'text-blue-500' : ''}>
              {customer?.isBlocked ? 'Blocked' : 'Active'}
            </span> */}
          </>
        );
        // <span className={customer?.isEmployee ? 'text-blue-500' : ''}>
        //   {customer.isVerified ? 'Verified' : 'Unverified'}
        // </span>
      },
    },
    {
      key: 'email',
      header: 'Email',
      render: (customer) => (
        <span className={customer?.isEmployee ? 'text-blue-500' : ''}>
          {customer?.email}
        </span>
      ),
    },
    {
      key: 'orders',
      header: 'Orders',
      render: (customer) => (
        <span className={customer?.isEmployee ? 'text-blue-500' : ''}>
          {customer?.orderDetails?.orders}
        </span>
      ),
    },
    {
      key: 'isBlocked',
      header: 'Status',
      render: (customer) => {
        return (
          <>
            <StatusDropdown
              options={
                customer.isEmployee
                  ? [
                      {
                        label: 'Active',
                        value: 'active',
                        textClass: 'text-green-500',
                        bgClass: 'bg-green-500',
                      },
                      {
                        label: 'Blocked',
                        value: 'blocked',
                        textClass: 'text-red-500',
                        bgClass: 'bg-red-500',
                      },
                    ]
                  : []
              }
              canEditVal="user"
              value={customer?.isBlocked ? 'blocked' : 'active'}
              onChange={(status) =>
                setState((prev) => ({
                  ...prev,
                  selectedUserId: customer.id,
                  showStatusModel: true,
                  statusToUpdate: status,
                }))
              }
            />
            {/* <span className={customer.isEmployee ? 'text-blue-500' : ''}>
              {customer?.isBlocked ? 'Blocked' : 'Active'}
            </span> */}
          </>
        );
      },
    },
    // {
    //   key: 'loyaltyPoints',
    //   header: 'Loyalty Point',
    //   render: (customer) => (
    //     <span className={customer.isEmployee ? 'text-blue-500' : ''}>
    //       {customer?.loyalty?.points}
    //     </span>
    //   ),
    // },
    {
      key: 'amountSpent',
      header: 'Amount Spend (BHD)',
      render: (customer) => (
        <span className={customer?.isEmployee ? 'text-blue-500' : ''}>
          {toFixedThree(customer?.orderDetails?.spends)}
        </span>
      ),
    },
  ];

  const actions: Action<Customer>[] = [
    {
      text: 'View',
      onClick: (customer: Customer) => {
        navigate(`${RoutesLink.customerDetails}/${customer?.id}`);
      },
    },
  ];

  return (
    <Layout>
      <PageHeading text="Customer" />
      <Card>
        <div className="flex w-full mb-4 items-center justify-between gap-3 flex-wrap">
          <CommonFilter
            filters={filters}
            inputPlaceHolder="Search for Customers..."
            onSearch={(text) => setSearchValue(text)}
            onChange={(filter) => handleChangeTab(filter)}
          />
          <div className="flex items-center gap-3 flex-wrap">
            <DateRangePicker
              dateRange={dateRange}
              setDateRange={setDateRange}
            />
            <ExportOption
              APIPath={`${API_URL_USER_SERVICE}${API_ENDPOINT.users}/export`}
              dateRange={dateRange[0]}
              isEmployee={state.type === UserRoles.EMPLOYEE ? true : false}
              isStaff={state.type === UserRoles.STAFF ? true : false}
            />
          </div>
        </div>
        <CommonTable
          columns={customerColumns}
          data={state.customer}
          showCheckbox={false}
          selectedItems={selectedItems}
          onSelectAll={handleSelectAll}
          onSelectItem={handleSelectItem}
          getItemId={(item) => item.id}
          emptyMessage="No Customers found"
          loading={state.isLoading}
          actions={actions}
        />
        {state.pageInfo.totalPages > 1 && (
          <Pagination
            isLoading={state.isLoading}
            paginationData={state.pageInfo}
            onPageChange={(page) => handleChangePage(page)}
            onPageSizeChange={handleChangePageSize}
          />
        )}
        {state.showStatusModel && (
          <ConfirmationModel
            modelType="update"
            show={state.showStatusModel}
            onClose={() => {
              setState((prev) => ({
                ...prev,
                showStatusModel: false,
                selectedUserId: null,
              }));
            }}
            subTitle={`This action will update the status to ${state.statusToUpdate}`}
            onConfirm={() =>
              handleBlockUser(
                state.selectedUserId as string,
                state.statusToUpdate as 'blocked' | 'active',
              )
            }
          />
        )}
        {state.showVerificationModel && (
          <ConfirmationModel
            modelType="update"
            show={state.showVerificationModel}
            onClose={() => {
              setState((prev) => ({
                ...prev,
                statusToUpdate: null,
                showVerificationModel: false,
                selectedUserId: null,
              }));
            }}
            subTitle={`This action will update the status to ${state.statusToUpdate}`}
            onConfirm={() =>
              handleVerifyUser(
                state.selectedUserId as string,
                state.statusToUpdate as 'verified' | 'unVerified',
              )
            }
          />
        )}
      </Card>
    </Layout>
  );
};

export default Customers;
